import { useMemo, useRef, useEffect, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { emojis } from '@tiptap/extension-emoji';
import { buildEmojiGroups } from '@arthelper/shared/emoji-picker';

/**
 * Two modes, because two callers want opposite things from the same grid.
 *
 * The DM composer wants the glyph typed into a textarea at the cursor. The reaction bar
 * wants the glyph itself, to toggle — nothing is being composed. Discriminated rather
 * than a pair of optional props so the existing callers keep compiling untouched and
 * neither mode can be half-supplied.
 */
type EmojiPickerPopoverProps =
  | {
      textareaRef: RefObject<HTMLTextAreaElement | null>;
      value: string;
      onChange: (value: string) => void;
      onClose: () => void;
      onSelect?: never;
    }
  | {
      onSelect: (emoji: string) => void;
      onClose: () => void;
      textareaRef?: never;
      value?: never;
      onChange?: never;
    };

export function EmojiPickerPopover(props: EmojiPickerPopoverProps) {
  const { t } = useTranslation('social');
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const groups = useMemo(() => buildEmojiGroups(emojis, search), [search]);

  function pick(emoji: string) {
    if (props.onSelect) {
      props.onSelect(emoji);
      props.onClose();
      return;
    }

    const { textareaRef, value, onChange, onClose } = props;
    const el = textareaRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + emoji + value.slice(end));
    onClose();
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  }

  return (
    <div>
      <div className="p-2 border-b border-gray-100">
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('dm.emoji-picker.search-placeholder', 'Search emojis...')}
          className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-black"
        />
      </div>

      <div className="overflow-y-auto max-h-52 p-2">
        {groups.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">{t('dm.emoji-picker.no-results', 'No emojis found')}</p>
        )}
        {groups.map((group) => (
          <div key={group.key} className="mb-2">
            {group.label && (
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide px-1 mb-1">{group.label}</p>
            )}
            <div className="grid grid-cols-8 gap-0.5">
              {group.items.map((item) => (
                <button
                  key={item.name}
                  type="button"
                  title={`:${item.shortcode}:`}
                  onClick={() => pick(item.emoji)}
                  className="text-xl leading-none p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  {item.emoji}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
